import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  PlusOutlined,
} from '@ant-design/icons';
import { Flex } from '@revenuemarket/cds';
import { colorTokens, semanticColors } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays } from '@revenuemarket/overlays';
import { AGGrid, AGGridSingleSelectCellEditor, Toast } from '@revenuemarket/ui';
import {
  CellValueChangedEvent,
  ColDef,
  GridApi,
  GridReadyEvent,
  ProcessCellForExportParams,
} from 'ag-grid-community';
import { Button, Result, Typography } from 'antd';

import { WIRING_RESULT_STATUS, WiringResultItem, WiringType } from '~apis/wiring';
import AGGridBusinessEntityEditor from '~components/ui/AGGrid/editors/AGGridBusinessEntityEditor';
import AGGridTransactionLabelSelectEditor from '~components/ui/AGGrid/editors/AGGridTransactionLabelSelectEditor';
import AGGridBusinessEntityRenderer from '~components/ui/AGGrid/renderers/AGGridBusinessEntityRenderer';
import AGGridCustomLabelRenderer from '~components/ui/AGGrid/renderers/AGGridCustomLabelRenderer';
import AGGridTransactionLabelSelectRenderer from '~components/ui/AGGrid/renderers/AGGridTransactionLabelSelectRenderer';
import AGGridwiringStatusRenderer from '~components/ui/AGGrid/renderers/AGGridWiringStatusRenderer';
import { useCategories } from '~hooks/queries/categories/query';
import { useGroupLabels } from '~hooks/queries/custom-labels/query';
import useBusinessEntityListQuery from '~hooks/queries/useBusinessEntityListQuery';
import { useReserveLabeling, useWiringExecutionCheck } from '~hooks/queries/wiring/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { CashFlowType } from '~models/cashFlow';
import { mixpanel } from '~pages/_app';

import { WiringBankConnectModel } from './types';
import CustomLabelSettingModal from '../CashFlow/CustomLabelSettingModal';

interface Props {
  subTitle?: string;
  wiringType?: WiringType;
  resultItems?: WiringResultItem[];
  defaultStatus?: WIRING_RESULT_STATUS;
  model: WiringBankConnectModel;
  wiringId: number;
  onCancel: (e: any) => void;
  onSuccess?: () => void;
  errorMessage?: string;
  onError?: ({ errorMessage, errorCode }: { errorMessage: string; errorCode: string }) => void;
}

export const getDataForResult = (
  status?: WIRING_RESULT_STATUS,
  description?: string,
  model?: WiringBankConnectModel,
  wiringType?: WiringType,
) => {
  const wiringBank = model?.wiringBank;
  const titleRenderer = () => {
    const isCompressed = model?.compressInfo?.isCompress;
    switch (wiringType) {
      case 'SALARY':
        return '급여이체' + (isCompressed ? ' (압축기장)' : '');
      case 'BULK':
        return '대량이체' + (isCompressed ? ' (압축기장)' : '');
      default:
        return '일반이체' + (isCompressed ? ' (압축기장)' : '');
    }
  };
  switch (status) {
    case 'SUCCESS':
      return {
        title: titleRenderer() + '가 완료되었습니다',
        icon: <CheckCircleFilled style={{ color: semanticColors.success.medium, fontSize: 35 }} />,
        description,
      };
    case 'PROCESSING':
      return {
        title:
          wiringBank === 'KB' && model?.reservationInfo?.reservationDate
            ? titleRenderer() + '가 예약되었습니다'
            : titleRenderer() + ' 대기중입니다',
        icon:
          wiringBank === 'KB' && model?.reservationInfo?.reservationDate ? (
            <CheckCircleFilled style={{ color: semanticColors.success.medium, fontSize: 35 }} />
          ) : (
            <ClockCircleFilled style={{ color: '#FAAD14', fontSize: 35 }} />
          ),
        description:
          wiringBank === 'KB'
            ? model?.reservationInfo?.reservationDate
              ? `
        ${model?.reservationInfo?.reservationDate} ${model?.reservationInfo?.reservationTime}에 이체가 실행됩니다.
        `
              : '이체 중입니다. 최대 1분 정도 소요됩니다.'
            : '잠시만 기다려주세요. 예약 이체일 경우 입력하신 예약일시에 이체가 진행됩니다.',
      };
    case 'PARTIAL_FAILED':
    case 'FAILED':
      return {
        title: titleRenderer() + ' 실패한 항목이 있습니다',
        icon: <CloseCircleFilled style={{ color: semanticColors.danger.medium, fontSize: 35 }} />,
        description: '아래 내역을 확인하고, 실패한 항목은 새 이체를 작성해주세요.',
      };
    default:
      return {
        title:
          wiringBank === 'KB' && model?.reservationInfo?.reservationDate
            ? titleRenderer() + '가 예약되었습니다'
            : titleRenderer() + ' 대기중입니다',
        icon:
          wiringBank === 'KB' && model?.reservationInfo?.reservationDate ? (
            <CheckCircleFilled style={{ color: semanticColors.success.medium, fontSize: 35 }} />
          ) : (
            <ClockCircleFilled style={{ color: '#FAAD14', fontSize: 35 }} />
          ),
        description:
          wiringBank === 'KB'
            ? model?.reservationInfo?.reservationDate
              ? `
      ${model?.reservationInfo?.reservationDate} ${model?.reservationInfo?.reservationTime}에 이체가 실행됩니다.
      `
              : '이체 중입니다. 잠시만 기다려주세요.'
            : '잠시만 기다려주세요. 예약 이체일 경우 입력하신 예약일시에 이체가 진행됩니다.',
      };
  }
};

const getTableTitle = ({
  status,
  totalNum,
  targetNum,
}: {
  status: WIRING_RESULT_STATUS;
  totalNum: number;
  targetNum?: number;
}) => {
  switch (status) {
    case 'SUCCESS':
      return `총 ${totalNum}건`;
    case 'PROCESSING':
      return `총 ${totalNum}건 중 ${targetNum}건 대기`;
    case 'PARTIAL_FAILED':
    case 'FAILED':
      return `총 ${totalNum}건 중 ${targetNum}건 실패`;
  }
};

const WiringComplete: React.FC<Props> = ({
  subTitle,
  wiringId,
  wiringType,
  onCancel,
  defaultStatus,
  resultItems = [],
  model,
  onSuccess,
  errorMessage,
  onError,
}) => {
  const [gridApi, setGridApi] = useState<GridApi>();
  const [status, setStatus] = useState<WIRING_RESULT_STATUS>(defaultStatus ?? 'PROCESSING');
  const { companyId } = useCompanyId();

  const [shouldRefetch, setShouldRefetch] = useState(true);
  const { mutateAsync: executeWiring } = useWiringExecutionCheck();
  const { data: categoriesData } = useCategories({ companyId });
  const { data: businessEntityData } = useBusinessEntityListQuery({
    companyId: companyId as string,
    favoriteOnly: false,
  });
  const { data: customLabelsData } = useGroupLabels({ companyId });
  const { mutateAsync: reserveLabeling } = useReserveLabeling();

  const onClearFocusedCell = useCallback(() => {
    gridApi?.clearFocusedCell();
  }, [gridApi]);

  const customLabelOptions = useMemo(
    () =>
      customLabelsData?.Data?.data
        ?.map((customLabel) => ({
          label: customLabel.name,
          value: customLabel.id,
          description: customLabel.description,
        }))
        .concat([
          {
            label: '그룹 분류 없는 거래내역',
            value: 0,
            description: '그룹 분류 없는 거래내역으로 되돌립니다.',
          },
        ]),
    [customLabelsData],
  );

  const categories = useMemo(
    () => categoriesData?.Data?.data?.categories ?? [],
    [categoriesData?.Data?.data?.categories],
  );

  const onGridReady = useCallback(({ api }: GridReadyEvent) => {
    setGridApi(api);
    api.sizeColumnsToFit();
  }, []);

  const columnDefs = useMemo<ColDef[]>(() => {
    if (!gridApi) {
      return [];
    }

    return [
      {
        headerName: '상태',
        field: 'status',
        width: 80,
        cellRenderer: AGGridwiringStatusRenderer,
        cellClass: css`
          background: rgba(0, 0, 0, 0.06);
          display: flex;
          align-items: center;
          font-size: 14px;
        `,
      },
      {
        headerName: '적요',
        field: 'senderDescription',
        width: 150,
        cellClass: css`
          background: rgba(0, 0, 0, 0.06);
          display: flex;
          align-items: center;
          font-size: 14px;
        `,
      },
      {
        headerName: '거래자명',
        field: 'receiverName',
        cellClass: css`
          background: rgba(0, 0, 0, 0.06);
          display: flex;
          align-items: center;
          font-size: 14px;
        `,
      },
      {
        headerName: '메모',
        field: 'memo',
        width: 100,
        cellClass: css`
          background: rgba(0, 0, 0, 0.06);
          display: flex;
          align-items: center;
          font-size: 14px;
        `,
      },
      {
        headerName: '출금액',
        width: 150,
        field: 'amount',
        cellClass: css`
          background: rgba(0, 0, 0, 0.06);
          display: flex;
          align-items: center;
          justify-content: flex-end;
          font-size: 14px;
        `,
        headerComponentParams: {
          align: 'right',
        },
        valueFormatter: (params) => {
          return params.value ? `${params.value.toLocaleString()}` : '';
        },
      },
      {
        headerName: '계정',
        field: 'categoryLabelKey',
        cellRenderer: AGGridTransactionLabelSelectRenderer,
        cellRendererParams: {
          skipAnalysis: true,
        },
        cellEditorParams: {
          cashFlowType: CashFlowType.OUT,
          hideSplitLabel: true,
        },
        cellEditor: AGGridTransactionLabelSelectEditor,
        editable: true,
        suppressKeyboardEvent: (params) => {
          if (params.event.code === 'Space') {
            params.api.startEditingCell({
              rowIndex: params.node.rowIndex as number,
              colKey: params.column.getColId(),
            });
          }
          return params.editing && params.event.key === 'Enter';
        },
        valueSetter: ({ data, oldValue, newValue }) => {
          if (newValue == null || oldValue === newValue) {
            return false;
          }
          const transactionType = CashFlowType.OUT;
          const filteredCategories = categories.filter(
            (category) => category.cashFlowType === transactionType,
          );

          if (filteredCategories?.some((category) => category.categoryLabelKey === newValue)) {
            data.categoryLabelKey = newValue;
            return true;
          }
          return false;
        },
      },
      {
        headerName: '거래처',
        field: 'businessEntityId',
        colId: 'businessEntityId',
        cellRenderer: AGGridBusinessEntityRenderer,
        cellEditor: AGGridBusinessEntityEditor,
        valueFormatter: (params) => {
          return params.data?.businessEntityLabel ?? '';
        },
        valueSetter: ({ data, oldValue, newValue }) => {
          const targetBusinessEntity = businessEntityData?.Data?.data?.find(
            (d) => d.id === newValue,
          );
          data.businessEntityLabel = targetBusinessEntity?.name ?? '';
          data.businessEntityId = newValue ?? null;
          return true;
        },
        editable: true,
        suppressKeyboardEvent: (params) => {
          if (params.event.code === 'Space') {
            params.api.startEditingCell({
              rowIndex: params.node.rowIndex as number,
              colKey: params.column.getColId(),
            });
          }
          return params.editing && params.event.key === 'Enter';
        },
      },
      {
        headerName: '그룹',
        field: 'customLabelId',
        colId: 'customLabelId',
        suppressKeyboardEvent: (params) => {
          if (params.event.code === 'Space') {
            params.api.startEditingCell({
              rowIndex: params.node.rowIndex as number,
              colKey: params.column.getColId(),
            });
          }
          return params.editing && params.event.key === 'Enter';
        },
        cellEditor: AGGridSingleSelectCellEditor,
        cellEditorParams: {
          options: customLabelOptions,
          footer: (
            <Button
              icon={<PlusOutlined />}
              style={{ flex: 1 }}
              onClick={() => {
                mixpanel.track(MIXPANEL_EVENT.LABEL.CLICK_ADD_GROUP_LABEL, {
                  page: location.pathname,
                });
                overlays.open(({ isOpen, onClose }) => (
                  <CustomLabelSettingModal
                    title="그룹 분류 설정"
                    open={isOpen}
                    onCancel={onClose}
                  />
                ));
              }}
            >
              그룹 분류 추가
            </Button>
          ),
        },
        cellRenderer: AGGridCustomLabelRenderer,
        cellRendererParams: {
          options: customLabelOptions,
        },
        editable: true,
        valueSetter: ({ data, oldValue, newValue }) => {
          if (oldValue === newValue) {
            return false;
          }
          if (newValue == null) {
            data.customLabelId = null;
            return true;
          }
          if (customLabelOptions?.some((customLabel) => customLabel.value === Number(newValue))) {
            data.customLabelId = Number(newValue);
            return true;
          }
          return false;
        },
      },
    ];
  }, [gridApi, categories, customLabelOptions, businessEntityData, overlays]);

  const onCellValueChanged = useCallback((e: CellValueChangedEvent<WiringResultItem>) => {
    if (e.source === 'undo') return;
    switch (e.column.getColDef().field) {
      case 'categoryLabelKey':
        if (e.newValue == null) {
          e.api.undoCellEditing();
          return;
        }
        break;
      case 'businessEntityId':
        if (e.newValue == null) {
          e.api.undoCellEditing();
          return;
        }
        break;
      case 'customLabelId':
        if (e.newValue == null) {
          e.api.undoCellEditing();
          return;
        }
        break;
    }
  }, []);

  const processCellForClipboard = useCallback(
    (e: ProcessCellForExportParams<WiringResultItem>) => {
      switch (e.column.getColDef().field) {
        case 'categoryLabelKey':
          return e.value;
        case 'amount':
          return e.value ? Number(e.value) : '';
        case 'customLabelId':
          const customLabel = customLabelOptions?.find((option) => option.value === e.value);
          return customLabel?.label;
        case 'businessEntityId':
          return e.value;
      }
    },
    [categoriesData?.Data?.data?.categories, customLabelOptions],
  );

  const processCellFromClipboard = useCallback(
    (e: ProcessCellForExportParams<WiringResultItem>) => {
      switch (e.column.getColDef().field) {
        case 'categoryLabelKey':
          return e.value;
        case 'customLabelId':
          if (!Number.isNaN(Number(e.value))) {
            return Number(e.value);
          }
          const customLabel = customLabelOptions?.find((option) => option.label === e.value);
          return customLabel?.value;
        case 'businessEntityId':
          return Number(e.value);
        case 'amount':
          return e.value ? Number(e.value.replace(/,/g, '')) : '';
        default:
          return e.value;
      }
    },
    [categoriesData?.Data?.data?.categories, customLabelOptions],
  );

  const onComplete = async () => {
    const items =
      (gridApi?.getRenderedNodes().map((node) => node.data) as WiringResultItem[]) ?? [];

    if (items.length === 0) return;

    const response = await reserveLabeling({
      companyId,
      request: {
        wiringId,
        recordLabelingList: items.map((item) => ({
          recordId: item.id,
          categoryLabelKey: item.categoryLabelKey,
          businessEntityId: item.businessEntityId,
          customLabelId: item.customLabelId,
        })),
      },
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      mixpanel.track(MIXPANEL_EVENT.LABEL.ADD_LABEL, {
        type: 'wiring',
        transactions: items.map(({ categoryLabelKey }) => ({
          categoryId: categoryLabelKey?.split('_')[0],
        })),
      });
      mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
        type: 'add label',
      });

      onCancel(null);
      onClearFocusedCell();
      Toast.success('분류가 완료되었습니다.');
    } else {
      Toast.error(response.ErrorMsg);
    }
  };

  useEffect(() => {
    if (shouldRefetch && companyId && wiringId && gridApi) {
      const intervalId = setInterval(async () => {
        const response = await executeWiring({ companyId, wiringId });
        const status = response?.Data?.data?.status;
        const items = response?.Data?.data?.recordList || [];
        items.forEach((item, index) => {
          const target = gridApi?.getRowNode(index.toString());
          target?.setData({
            ...target.data,
            status: item.status,
          });
        });

        if (response?.Data?.data.resultCode && response?.Data?.data.resultMessage) {
          onError?.({
            errorMessage: response.Data.data.resultMessage,
            errorCode: response.Data.data.resultCode,
          });
        }

        if (status !== undefined) {
          setStatus(status);
        }

        // 특정 column들만 값변경

        // 특정 응답 조건 체크
        if (status === 'SUCCESS' || status === 'FAILED' || status === 'PARTIAL_FAILED') {
          setShouldRefetch(false); // 조건이 만족되면 반복 중지
          clearInterval(intervalId);
          onSuccess?.();
        }
      }, 5000); // 5초마다 요청

      // 컴포넌트 언마운트 시 정리
      return () => clearInterval(intervalId);
    }
  }, [shouldRefetch, executeWiring, companyId, wiringId, gridApi, onError]);

  useEffect(() => {
    return () => {
      if (onClearFocusedCell) {
        onClearFocusedCell();
      }
    };
  }, [onClearFocusedCell]);

  const dataForResult = getDataForResult(status, subTitle, model, wiringType);

  return (
    <Flex direction="column" gap={24} style={{ padding: 24 }}>
      <Result
        style={{
          padding: 0,
        }}
        status={'success'}
        title={<Typography.Title level={4}>{dataForResult.title}</Typography.Title>}
        subTitle={
          <Flex direction="column" gap={4}>
            <Typography.Text
              style={{
                color: colorTokens.textDescription,
              }}
            >
              {dataForResult.description}
            </Typography.Text>
            {errorMessage && (
              <Typography.Text
                style={{
                  color: semanticColors.danger.medium,
                }}
              >
                {errorMessage}
              </Typography.Text>
            )}
          </Flex>
        }
        icon={dataForResult.icon}
      />

      <Flex
        style={{
          padding: '10px 16px',
          background: colorTokens.bgPrimary,
        }}
        gap={8}
        align="center"
      >
        <Typography>
          <b>미리 분류할 수 있어요.</b> 잊기 전에 분류 해보세요. 만약 분류 하지 않더라도 클로브가
          자동으로 분류해드려요.
        </Typography>
      </Flex>
      {status && (
        <Typography.Text
          style={{
            color: status.includes('FAILED') ? semanticColors.danger.medium : 'black',
          }}
        >
          {getTableTitle({
            status,
            totalNum: resultItems.length,
            targetNum: resultItems.filter((item) =>
              status === 'PROCESSING'
                ? item?.status === 'WAITING'
                : status === 'PARTIAL_FAILED'
                  ? item.status === 'FAILED'
                  : status === item.status,
            ).length,
          })}
        </Typography.Text>
      )}
      <AGGrid<WiringResultItem>
        containerStyle={{
          height: '30vh',
        }}
        className={css`
          .ag-root-wrapper {
            border-left: none;
            border-right: none;
            border-radius: 0;
          }
        `}
        singleClickEdit
        id="wiring"
        enableFillHandle
        enableRangeHandle
        enableRangeSelection
        fillHandleDirection="y"
        rowSelection="multiple"
        onGridReady={onGridReady}
        columnDefs={columnDefs}
        rowData={resultItems}
        onCellValueChanged={onCellValueChanged}
        processCellForClipboard={processCellForClipboard}
        processCellFromClipboard={processCellFromClipboard}
        headerHeight={32}
        rowHeight={56}
      />
      <Flex justify="flex-end" gap={8} align="center">
        <Button
          onClick={() => {
            //@ts-ignore
            onCancel?.();
            onClearFocusedCell();
          }}
        >
          분류 건너뛰기
        </Button>
        <Button type="primary" onClick={onComplete}>
          완료
        </Button>
      </Flex>
    </Flex>
  );
};

export default WiringComplete;
